import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  dashboardLayoutsQueryKey,
  type DashboardLayoutsResponse,
} from '@/components/reporting/dashboard-layout-content';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';

export const useDashboardLayouts = () => {
  const queryClient = useQueryClient();
  const churchId = useChurchStore((state) => state.church?.id);
  const isCongregant = useChurchStore((state) => state.membership?.systemRole === 'congregant');
  const layoutsQueryKey = dashboardLayoutsQueryKey(churchId);

  const query = useQuery({
    queryKey: layoutsQueryKey,
    queryFn: () => api.get<DashboardLayoutsResponse>('/dashboard/layouts'),
    enabled: Boolean(churchId) && !isCongregant,
  });

  const activateMutation = useMutation({
    mutationFn: (layoutId: string) =>
      api.post<DashboardLayoutsResponse>(`/dashboard/layouts/${layoutId}/activate`),
    onSuccess: (data) => {
      queryClient.setQueryData(layoutsQueryKey, data);
    },
  });

  const activeLayoutIndex = useMemo(() => {
    if (!query.data) return 0;
    const index = query.data.layouts.findIndex((layout) => layout.id === query.data.activeLayoutId);
    return index >= 0 ? index : 0;
  }, [query.data]);

  const activeLayout = query.data?.layouts[activeLayoutIndex] ?? null;

  return {
    query,
    activateMutation,
    activeLayout,
    activeLayoutIndex,
    layouts: query.data?.layouts ?? [],
    activeLayoutId: query.data?.activeLayoutId,
  };
};
